import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2, CheckCircle2, QrCode, Banknote } from "lucide-react";
import { QrHelp } from "@/components/QrHelp";
import { toast } from "sonner";

// Manual payment flow: choose WOI QR / cash -> mark paid -> admin confirms.
export function PaymentDialog({ order, open, onClose, onDone }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paid, setPaid] = useState(false);
  const [method, setMethod] = useState("woi");

  useEffect(() => {
    if (open) {
      setPaid(false);
      setMethod("woi");
      api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => {});
    }
  }, [open]);

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/orders/${order.payment_order_id}/mark-paid`, { method });
      setPaid(true);
      toast.success("Payment submitted! Our team will confirm shortly.");
      onDone && onDone();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-turquoise" /> Secure Payment
          </DialogTitle>
          <DialogDescription>Choose how you're paying, then tap "I have paid" — our team confirms your spot.</DialogDescription>
        </DialogHeader>

        {paid ? (
          <div className="text-center py-6" data-testid="payment-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <p className="font-display font-bold text-lg text-navy">Payment Submitted</p>
            <p className="text-slate-500 text-sm mt-1">Your order is <b>awaiting confirmation</b>. You'll see it update in Mission Control once our team confirms it.</p>
            <Button onClick={onClose} className="mt-5 bg-navy text-white rounded-full" data-testid="payment-done-btn">Back to Mission Control</Button>
          </div>
        ) : (
          <div className="text-center">
            <p className="text-3xl font-display font-extrabold text-navy">${Number(order?.amount || 0).toFixed(2)}</p>
            <p className="text-xs text-slate-500 mb-3">BMD · Adventure Rock Bermuda</p>

            <div className="grid grid-cols-2 gap-2 mb-3" data-testid="payment-method-choice">
              {[["woi", "WOI QR code", QrCode], ["cash", "Cash", Banknote]].map(([k, l, Ico]) => (
                <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`pay-method-${k}`} className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm font-semibold transition-colors ${method === k ? "border-turquoise bg-turquoise/10 text-navy" : "border-slate-200 text-slate-500 hover:border-turquoise/50"}`}>
                  <Ico className="w-4 h-4" /> {l}
                </button>
              ))}
            </div>

            {order?.reference && (
              <div className="bg-gold/15 border-2 border-dashed border-gold rounded-xl px-3 py-2 mb-3" data-testid="payment-reference">
                <p className="text-[11px] text-slate-500 uppercase tracking-wider">Use this payment reference</p>
                <p className="font-mono font-extrabold text-lg text-navy tracking-wide">{order.reference}</p>
                <p className="text-[11px] text-slate-500">Add it to your {method === "cash" ? "cash payment" : "WOI payment"} note so we can match your payment.</p>
              </div>
            )}
            {order?.payment_due && (
              <div className={`rounded-xl px-3 py-2 text-[13px] font-semibold mb-3 ${order.overdue ? "bg-red-100 border border-red-300 text-red-700" : "bg-coral/10 border border-coral/30 text-coral"}`} data-testid="payment-due-note">
                ⏰ {order.overdue ? "Payment overdue —" : "Pay by"} {new Date(order.payment_due).toLocaleString("en", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })} to secure your spot.
              </div>
            )}
            <p className="text-[11px] text-slate-500 leading-relaxed mb-3" data-testid="payment-policy-note">Fees are paid in advance to secure your Explorer's space. Weekly is due Thursday 6 PM for the following week; Monthly 5 days before the month; Day passes &amp; transport 24 hours ahead; Dinner by 6 PM the day before; Camps 7 days before. Unpaid bookings may be released once capacity is reached.</p>
            {method === "woi" && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center" data-testid="woi-details">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Scan to pay with WOI</p>
                <img src={cfg?.qr_url || "/img/woi-qr.png"} alt="WOI payment QR code" className="w-44 h-44 mx-auto rounded-xl bg-white p-2 border" data-testid="woi-qr-image" />
                <p className="text-sm text-navy mt-2">Open your WOI app, scan the code, enter the amount and your reference. Then tap "I have paid" below.</p>
                <QrHelp src={cfg?.qr_url || "/img/woi-qr.png"} />
              </div>
            )}
            {method === "cash" && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 text-left" data-testid="cash-details">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Pay by cash</p>
                <p className="text-sm text-navy leading-relaxed">Pay by cash at Adventure Rock. Please bring the exact amount and mention your payment reference. Our team will confirm your spot once it's received.</p>
              </div>
            )}
            <p className="text-slate-600 text-sm mt-4 leading-relaxed">{cfg?.instructions}</p>
            <Button onClick={markPaid} disabled={busy} className="w-full mt-5 bg-turquoise hover:bg-turquoise/90 text-navy font-bold rounded-full" data-testid="payment-mark-paid-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "I have paid"}
            </Button>
            <button onClick={onClose} className="mt-3 text-slate-400 hover:text-slate-600 text-sm" data-testid="payment-cancel-btn">Cancel</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
